import { useState } from "react";
import { Inbox, Phone, Mail, Send, Loader2, CheckCircle2, Link2, Check } from "lucide-react";
import { formatEmailDate, formatDate, EMPTY_BOX, ALERT_BOX, LEAD_STATUSES } from "./format";
import { ExportButton } from "./ExportButton";
import { LeadAiPanel } from "./LeadAiPanel";
import { DeliveryBox } from "./DeliveryBox";
import { PreviewBox } from "./PreviewBox";

const FOLLOW_UP_DAYS = 3;
const daysSince = (iso) => (Date.now() - new Date(iso).getTime()) / 86400000;
const statusOf = (lead) => LEAD_STATUSES.find((s) => s.value === (lead.status || "nouvelle")) || LEAD_STATUSES[0];

const StatusSelect = ({ lead, i, onChange }) => {
  const current = statusOf(lead);
  return (
    <select
      data-testid={`admin-lead-status-${i}`}
      value={current.value}
      onChange={(e) => onChange(lead.id, e.target.value)}
      aria-label="Statut de la demande"
      className={`cursor-pointer appearance-none rounded-full border-0 px-3 py-1 pr-6 text-[11px] font-semibold outline-none ring-offset-1 focus:ring-2 focus:ring-gold ${current.cls}`}
      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}
    >
      {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
};

const StatusFilter = ({ leads, value, onChange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="admin-lead-filters">
    {[{ value: "all", label: "Toutes" }, ...LEAD_STATUSES].map((s) => {
      const count = s.value === "all" ? leads.length : leads.filter((l) => (l.status || "nouvelle") === s.value).length;
      const active = value === s.value;
      return (
        <button
          key={s.value}
          data-testid={`admin-lead-filter-${s.value}`}
          onClick={() => onChange(s.value)}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${active ? "bg-navy text-white shadow-md shadow-navy/20" : "border border-navy/15 text-slate-600 hover:border-gold hover:text-navy"}`}
        >
          {s.label} <span className={`ml-1 font-mono text-[10px] ${active ? "text-gold" : "text-slate-400"}`}>{count}</span>
        </button>
      );
    })}
  </div>
);

const FollowUp = ({ lead, i, onFollowUp }) => {
  const [sending, setSending] = useState(false);
  if (!lead.email) return null;
  if (lead.follow_up_sent_at) {
    return (
      <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1.5 text-[11px] font-semibold text-gold-dark" data-testid={`admin-lead-followed-${i}`}>
        <CheckCircle2 size={11} /> Relancée le {formatDate(lead.follow_up_sent_at)}
      </p>
    );
  }
  const age = daysSince(lead.created_at);
  if (age < FOLLOW_UP_DAYS) {
    const remaining = Math.ceil(FOLLOW_UP_DAYS - age);
    return (
      <p className="mt-4 text-[11px] text-slate-400" data-testid={`admin-lead-followup-wait-${i}`}>
        Relance possible dans {remaining} jour{remaining > 1 ? "s" : ""}
      </p>
    );
  }
  const send = async () => {
    if (sending) return;
    setSending(true);
    await onFollowUp(lead.id);
    setSending(false);
  };
  return (
    <button
      data-testid={`admin-lead-followup-${i}`}
      onClick={send}
      disabled={sending}
      className="mt-4 flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-navy-light disabled:opacity-60"
    >
      {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
      {sending ? "Envoi…" : "Relancer par email"}
    </button>
  );
};

const TrackingLink = ({ token, i }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/suivi/${token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* clipboard indisponible */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      data-testid={`admin-lead-tracking-${i}`}
      onClick={copy}
      title={url}
      className="flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-gold hover:text-gold-dark"
    >
      {copied ? <Check size={11} /> : <Link2 size={11} />} {copied ? "Lien copié !" : "Lien de suivi client"}
    </button>
  );
};

const LeadCard = ({ lead, i, onFollowUp, onStatus, onAnalyze, onSaveBrief, onDeliver, onSendPreview }) => {
  const closed = ["signee", "en_construction", "publie", "perdue"].includes(lead.status);
  const inProduction = ["signee", "en_construction", "publie"].includes(lead.status);
  const stale = !closed && !lead.follow_up_sent_at && lead.email && daysSince(lead.created_at) >= FOLLOW_UP_DAYS;
  return (
    <article
      data-testid={`admin-lead-card-${i}`}
      className={`rounded-3xl border bg-white p-6 transition-all hover:border-gold/40 hover:shadow-lg hover:shadow-gold/10 ${stale ? "border-gold/40" : "border-navy/10"} ${lead.status === "perdue" ? "opacity-70" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-navy">{lead.name}</h2>
        <time className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-400">{formatEmailDate(lead.created_at)}</time>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusSelect lead={lead} i={i} onChange={onStatus} />
        {lead.offer && (
          <span className="rounded-full bg-gold/15 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-gold-dark">{lead.offer}</span>
        )}
        {lead.sector && <span className="rounded-full bg-navy/5 px-3 py-1 text-[11px] font-medium text-slate-600">{lead.sector}</span>}
        {stale && <span className="rounded-full bg-gold px-3 py-1 text-[11px] font-semibold text-navy" data-testid={`admin-lead-stale-${i}`}>Sans réponse depuis {Math.floor(daysSince(lead.created_at))} j</span>}
        {lead.tracking_token && <TrackingLink token={lead.tracking_token} i={i} />}
      </div>
      <div className="mt-4 flex flex-col gap-1.5 text-sm">
        <a href={`tel:${lead.phone}`} data-testid={`admin-lead-phone-${i}`} className="flex items-center gap-2 font-medium text-navy transition-colors hover:text-gold-dark">
          <Phone size={14} className="text-gold-dark" /> {lead.phone}
        </a>
        {lead.email && (
          <a href={`mailto:${lead.email}`} data-testid={`admin-lead-email-${i}`} className="flex items-center gap-2 text-slate-600 transition-colors hover:text-gold-dark">
            <Mail size={14} className="text-gold-dark" /> {lead.email}
          </a>
        )}
      </div>
      {lead.message && <p className="mt-4 border-t border-navy/10 pt-4 text-sm leading-relaxed text-slate-600">{lead.message}</p>}
      <LeadAiPanel lead={lead} i={i} onAnalyze={onAnalyze} onSaveBrief={onSaveBrief} />
      {inProduction && <PreviewBox lead={lead} i={i} onSendPreview={onSendPreview} />}
      {inProduction && <DeliveryBox lead={lead} i={i} onDeliver={onDeliver} />}
      {!closed && <FollowUp lead={lead} i={i} onFollowUp={onFollowUp} />}
    </article>
  );
};

export const LeadsTab = ({ leads, error, onFollowUp, onStatus, onAnalyze, onSaveBrief, onDeliver, onSendPreview }) => {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? leads : leads.filter((l) => (l.status || "nouvelle") === filter);
  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-navy text-gold">
            <Inbox size={17} />
          </span>
          <h1 className="font-display text-2xl font-bold text-navy">Demandes reçues</h1>
        </div>
        <ExportButton resource="leads" filename="demandes.csv" label="Exporter en CSV" />
      </div>
      {leads.length > 0 && (
        <div className="mt-6">
          <StatusFilter leads={leads} value={filter} onChange={setFilter} />
        </div>
      )}
      {error && <p className={`mt-6 ${ALERT_BOX}`} data-testid="admin-error">{error}</p>}
      {leads.length === 0 && !error ? (
        <p className={`mt-8 ${EMPTY_BOX} py-12`} data-testid="admin-leads-empty">
          Aucune demande pour le moment. Les demandes du formulaire de contact apparaîtront ici.
        </p>
      ) : visible.length === 0 ? (
        <p className={`mt-8 ${EMPTY_BOX}`} data-testid="admin-leads-filter-empty">Aucune demande avec ce statut.</p>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="admin-leads-list">
          {visible.map((lead) => {
            const i = leads.indexOf(lead);
            return <LeadCard key={lead.id} lead={lead} i={i} onFollowUp={onFollowUp} onStatus={onStatus} onAnalyze={onAnalyze} onSaveBrief={onSaveBrief} onDeliver={onDeliver} onSendPreview={onSendPreview} />;
          })}
        </div>
      )}
    </div>
  );
};
